import { useState, useEffect } from "react";
import api from "../../../utils/api";

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    OutlinedInput,
    FormHelperText,
    Button,
    Box,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    CircularProgress,
} from "@mui/material";

const initial = {
    name: "",
    start_date: "",
    end_date: "",
    teacher_id: "",
    course_id: "",
    lesson_type_id: "",
};

const DEFAULT_MAX_STUDENTS = 1;

export default function ClassroomModal({ open, onClose, row = null }) {
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(initial);
    const [teachers, setTeachers] = useState([]);
    const [courses, setCourses] = useState([]);
    const [lessonTypes, setLessonTypes] = useState([]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("admin/teachers", { params: { page: 1, perPage: 500 } });
                if (!cancelled) setTeachers(res.data?.data || []);
                const cRes = await api.get("admin/courses", { params: { page: 1, perPage: 500 } });
                if (!cancelled) setCourses(cRes.data?.data || []);
                const ltRes = await api.get("admin/lesson-types/list");
                if (!cancelled) setLessonTypes(ltRes.data?.data || []);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open]);

    useEffect(() => {
        if (row) {
            const tid = row.teacher_id ?? row.teacher_id;
            setForm({
                name: row.classroom_name || row.name || "",
                start_date: row.start_date ? String(row.start_date).slice(0, 10) : "",
                end_date: row.end_date ? String(row.end_date).slice(0, 10) : "",
                teacher_id: tid != null && tid !== "" ? String(tid) : "",
                course_id: row.course_id != null && row.course_id !== "" ? String(row.course_id) : "",
                lesson_type_id: row.lesson_type_id != null && row.lesson_type_id !== "" ? String(row.lesson_type_id) : "",
            });
        } else {
            setForm(initial);
        }
        setErrors({});
        setSaving(false);
    }, [row, open]);

    const handleSubmit = async () => {
        try {
            setSaving(true);
            const teacherId = form.teacher_id === "" ? null : Number(form.teacher_id);
            const courseId = form.course_id === "" ? null : Number(form.course_id);
            const lessonTypeId = form.lesson_type_id === "" ? null : Number(form.lesson_type_id);

            if (row?.id) {
                await api.put(`admin/classrooms/${row.id}`, {
                    name: form.name || null,
                    start_date: form.start_date || null,
                    end_date: form.end_date || null,
                    teacher_id: teacherId,
                });
            } else {
                await api.post("admin/classrooms", {
                    name: form.name,
                    max_students: DEFAULT_MAX_STUDENTS,
                    start_date: form.start_date,
                    end_date: form.end_date || null,
                    teacher_id: teacherId,
                    course_id: courseId,
                    lesson_type_id: lessonTypeId,
                });
            }

            onClose(true);
        } catch (e) {
            setErrors(e.errors || {});
        } finally {
            setSaving(false);
        }
    };

    const teacherError = errors.teacher_id?.[0] || errors.teacher_id?.[0];

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
            <DialogTitle>{!row ? "Create classroom" : "Edit classroom"}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <FormControl error={!!errors.name?.[0]}>
                        <FormLabel htmlFor="classroom_name">Name</FormLabel>
                        <OutlinedInput
                            id="classroom_name"
                            name="name"
                            fullWidth
                            value={form.name}
                            onChange={handleChange}
                        />
                        {errors.name && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.name}>
                                {errors.name?.[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.start_date?.[0]}>
                        <FormLabel htmlFor="classroom_start_date">Start date</FormLabel>
                        <OutlinedInput
                            id="classroom_start_date"
                            name="start_date"
                            type="date"
                            fullWidth
                            value={form.start_date}
                            onChange={handleChange}
                        />
                        {errors.start_date && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.start_date}>
                                {errors.start_date?.[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.end_date?.[0]}>
                        <FormLabel htmlFor="classroom_end_date">End date</FormLabel>
                        <OutlinedInput
                            id="classroom_end_date"
                            name="end_date"
                            type="date"
                            fullWidth
                            value={form.end_date}
                            onChange={handleChange}
                        />
                        {errors.end_date && (
                            <FormHelperText sx={{ m: 0.5 }} error={!!errors.end_date}>
                                {errors.end_date?.[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!teacherError}>
                        <FormLabel htmlFor="classroom_teacher_id">Teacher</FormLabel>
                        <Select
                            id="classroom_teacher_id"
                            name="teacher_id"
                            value={form.teacher_id}
                            onChange={handleChange}
                            error={!!teacherError}
                        >
                            <MenuItem value="">
                                <em>No teacher</em>
                            </MenuItem>
                            {teachers.map((t) => {
                                const full = `${t.first_name || ""} ${t.last_name || ""}`.trim();
                                return (
                                    <MenuItem key={t.id} value={String(t.id)}>
                                        {full || `Teacher #${t.id}`}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {teacherError && (
                            <FormHelperText sx={{ m: 0.5 }} error>
                                {teacherError}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl error={!!errors.course_id?.[0]}>
                        <FormLabel htmlFor="classroom_course_id">Course</FormLabel>
                        <Select
                            id="classroom_course_id"
                            name="course_id"
                            value={form.course_id}
                            onChange={handleChange}
                            error={!!errors.course_id?.[0]}
                            disabled={!!row}
                        >
                            <MenuItem value="">
                                <em>Select course</em>
                            </MenuItem>
                            {courses.map((c) => {
                                const courseName = c.name_en || c.name_ru || c.name_hy || `Course #${c.id}`;
                                return (
                                    <MenuItem key={c.id} value={String(c.id)}>
                                        {courseName}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {errors.course_id && (
                            <FormHelperText sx={{ m: 0.5 }} error>
                                {errors.course_id?.[0]}
                            </FormHelperText>
                        )}
                    </FormControl>

                    <FormControl required error={!!errors.lesson_type_id?.[0]}>
                        <FormLabel htmlFor="classroom_lesson_type_id">Lesson type *</FormLabel>
                        <Select
                            id="classroom_lesson_type_id"
                            name="lesson_type_id"
                            value={form.lesson_type_id}
                            onChange={handleChange}
                            error={!!errors.lesson_type_id?.[0]}
                            disabled={!!row}
                        >
                            <MenuItem value="">
                                <em>Select lesson type</em>
                            </MenuItem>
                            {lessonTypes.map((type) => (
                                <MenuItem key={type.id} value={String(type.id)}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {row ? (
                            <FormHelperText sx={{ m: 0.5 }}>
                                Lesson type cannot be changed after the classroom is created.
                            </FormHelperText>
                        ) : null}
                        {errors.lesson_type_id && (
                            <FormHelperText sx={{ m: 0.5 }} error>
                                {errors.lesson_type_id?.[0]}
                            </FormHelperText>
                        )}
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button
                    sx={{ color: "#fff" }}
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving || !form.lesson_type_id || (!row && !form.course_id)}
                >
                    {saving ? <CircularProgress size={24} /> : !row ? "Create" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
